/**
 * Decision Trace Expander
 *
 * Expandable rule trace list for a decision log entry.
 * Shows the ordered list of rules evaluated with PASS/FAIL/SKIP indicators.
 */
import { useState } from 'react';
import { Badge, cn } from '@respectly/ui';
import { ChevronDown, ChevronUp, BookOpen, UserPlus } from 'lucide-react';
import type {
  PRETraceEntry,
  PREConsentCaptureTraceEntry,
} from '@respectly/shared-types';

// ============================================
// Types
// ============================================

interface DecisionTraceExpanderProps {
  trace: PRETraceEntry[];
  consentCapture?: PREConsentCaptureTraceEntry[];
  className?: string;
}

// ============================================
// Helpers
// ============================================

const RESULT_CONFIG: Record<
  PRETraceEntry['result'],
  { variant: 'success' | 'error' | 'secondary'; label: string }
> = {
  PASS: { variant: 'success', label: 'PASS' },
  FAIL: { variant: 'error', label: 'FAIL' },
  SKIP: { variant: 'secondary', label: 'SKIP' },
};

// ============================================
// Component
// ============================================

export function DecisionTraceExpander({
  trace,
  consentCapture,
  className,
}: DecisionTraceExpanderProps) {
  const [isExpanded, setIsExpanded] = useState(false);

  if (trace.length === 0 && (!consentCapture || consentCapture.length === 0)) return null;

  return (
    <div className={cn('', className)}>
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="inline-flex items-center gap-1.5 text-xs text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-200 transition-colors"
      >
        <span className="font-medium">
          {trace.length} rule{trace.length !== 1 ? 's' : ''} evaluated
          {consentCapture && consentCapture.length > 0 && (
            <> · {consentCapture.length} auto-capture</>
          )}
        </span>
        {isExpanded ? (
          <ChevronUp className="w-3.5 h-3.5" />
        ) : (
          <ChevronDown className="w-3.5 h-3.5" />
        )}
      </button>

      {isExpanded && (
        <div className="mt-2 space-y-1.5">
          {consentCapture && consentCapture.length > 0 && (
            <div className="space-y-1 mb-3 pb-3 border-b border-slate-200 dark:border-slate-700">
              <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider font-semibold text-emerald-700 dark:text-emerald-400">
                <UserPlus className="w-3 h-3" />
                Auto-Capture Decisions
              </div>
              {consentCapture.map((c, i) => (
                <div
                  key={`cc-${c.ruleId}-${i}`}
                  className="flex items-center gap-2 text-xs py-1.5 px-2 rounded-md bg-emerald-50/60 dark:bg-emerald-900/20"
                >
                  <span className="font-mono text-[10px] text-slate-500 dark:text-slate-400 flex-shrink-0">
                    {c.ruleId}
                  </span>
                  <span className="flex-1 text-slate-700 dark:text-slate-300 truncate">
                    {c.ruleName}
                  </span>
                  <Badge variant="outline" className="text-[10px] flex-shrink-0">
                    {c.jurisdictionCode}
                  </Badge>
                  <Badge
                    variant={c.effect === 'ALLOW' ? 'success' : 'error'}
                    className="text-[10px] flex-shrink-0"
                  >
                    {c.effect}
                  </Badge>
                  <Badge
                    variant={c.result === 'PASS' ? 'info' : 'secondary'}
                    className="text-[10px] flex-shrink-0"
                  >
                    {c.result === 'PASS' ? 'Applied' : 'Overridden'}
                  </Badge>
                </div>
              ))}
            </div>
          )}
          {trace.map((entry, index) => {
            const config = RESULT_CONFIG[entry.result];
            return (
              <div
                key={`${entry.ruleId}-${index}`}
                className="flex items-center gap-2 text-xs py-1.5 px-2 rounded-md bg-slate-50 dark:bg-slate-800/50"
              >
                {/* Step number */}
                <span className="w-5 h-5 rounded-full bg-slate-200 dark:bg-slate-700 flex items-center justify-center text-[10px] font-semibold text-slate-600 dark:text-slate-400 flex-shrink-0 tabular-nums">
                  {index + 1}
                </span>

                {/* Rule ID */}
                <span className="font-mono text-[10px] text-slate-500 dark:text-slate-500 flex-shrink-0">
                  {entry.ruleId}
                </span>

                {/* Rule Name */}
                <span className="flex-1 text-slate-700 dark:text-slate-300 truncate">
                  {entry.ruleName}
                </span>

                {/* Source ref */}
                {entry.sourceRef && (
                  <span className="flex items-center gap-0.5 text-slate-400 dark:text-slate-500 flex-shrink-0">
                    <BookOpen className="w-3 h-3" />
                    <span className="text-[10px] truncate max-w-[80px]">
                      {entry.sourceRef}
                    </span>
                  </span>
                )}

                {/* Result badge */}
                <Badge
                  variant={config.variant}
                  className="text-[10px] flex-shrink-0"
                >
                  {config.label}
                </Badge>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
